import { useEffect, useSyncExternalStore } from "react";
import api from "../../../utils/api";
import type {
  BookingDocument,
  BookingEvent,
  BookingQuote,
  CargoBooking,
} from "../booking/bookingTypes";
import { makeFolio, runBooking, type PaymentDetails } from "../booking/bookingEngine";

// ============================================================================
// Cargo Market · Módulo A — bookings store (backend-backed, graceful fallback)
// ----------------------------------------------------------------------------
// Single reactive source of truth for cargo-space purchases, shared between the
// quoter's checkout (which creates a booking) and the "Reservas y booking"
// section (which lists them). Bookings persist to the `cargo_bookings`
// collection via the torre-de-control-api `cargoBookings` module.
//
// Like dealsService, mutations are optimistic and the store hydrates from the
// API on first use. Because Cargo Market is a brand-new module that ships ahead
// of a deployed backend, every API call degrades gracefully: if the request
// fails the booking still lives in the in-session store (seeded with a couple
// of examples) so the end-to-end demo always works.
// ============================================================================

const ENDPOINT = "/cargo/bookings";
const LIST_ENDPOINT = "/cargo/bookingsList";

// --- Firestore timestamp normalization (same shape as dealsService) ---------
interface FirestoreTimestamp {
  _seconds?: number;
  seconds?: number;
}

function toISODate(value: FirestoreTimestamp | string | null | undefined): string {
  if (!value) return "";
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value.slice(0, 10) : d.toISOString().slice(0, 10);
  }
  const seconds = value._seconds ?? value.seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000).toISOString().slice(0, 10);
  return "";
}

type RawBooking = Partial<CargoBooking> & {
  id: string;
  createdAt?: FirestoreTimestamp | string | null;
  sandboxOf?: string | null;
};

function fromApi(raw: RawBooking): CargoBooking {
  return {
    id: raw.id,
    folio: raw.folio || "—",
    route: raw.route || "",
    origin: raw.origin || "",
    destination: raw.destination || "",
    mode: raw.mode || "",
    incoterm: raw.incoterm || "",
    provider: raw.provider || "",
    amount: Number(raw.amount) || 0,
    currency: raw.currency || "MXN",
    status: raw.status || "reserving",
    paymentStatus: raw.paymentStatus || "pending",
    bookingRef: raw.bookingRef ?? null,
    awb: raw.awb ?? null,
    bl: raw.bl ?? null,
    documents: Array.isArray(raw.documents) ? (raw.documents as BookingDocument[]) : [],
    events: Array.isArray(raw.events) ? (raw.events as BookingEvent[]) : [],
    quoteBasis: raw.quoteBasis || "",
    transitDays: raw.transitDays || "",
    createdAt: toISODate(raw.createdAt) || new Date().toISOString().slice(0, 10),
    sandbox: Boolean(raw.sandboxOf),
  };
}

// --- Store ------------------------------------------------------------------

const SEED: CargoBooking[] = [
  {
    id: "seed-b1", folio: "BKG-0001", route: "MEX → MIA", origin: "MEX", destination: "MIA",
    mode: "Aérea", incoterm: "CIF", provider: "AeroCargo", amount: 13200, currency: "MXN",
    status: "confirmed", paymentStatus: "paid", bookingRef: "AER-LX9Q2-014",
    awb: "045-12345678", bl: null,
    documents: [
      { type: "booking", label: "Confirmación de reserva", reference: "AER-LX9Q2-014" },
      { type: "awb", label: "Guía aérea (AWB)", reference: "045-12345678" },
    ],
    events: [], quoteBasis: "850 kg facturables", transitDays: "2–5 días", createdAt: "2026-06-14",
  },
  {
    id: "seed-b2", folio: "BKG-0002", route: "Veracruz → Rotterdam", origin: "Veracruz",
    destination: "Rotterdam", mode: "Marítima", incoterm: "DDP", provider: "OceanLine",
    amount: 41200, currency: "MXN", status: "reserved", paymentStatus: "pending",
    bookingRef: "MAR-LX8R1-220", awb: null, bl: null, documents: [], events: [],
    quoteBasis: "40' HC", transitDays: "18–35 días", createdAt: "2026-06-15",
  },
];

let bookings: CargoBooking[] = SEED;
const listeners = new Set<() => void>();

type LoadState = "idle" | "loading" | "loaded";
let loadState: LoadState = "idle";

function emit() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): CargoBooking[] {
  return bookings;
}

/** Fetch all bookings once (idempotent). Failure leaves the seed in place. */
async function loadBookings(force = false): Promise<void> {
  if (loadState === "loading") return;
  if (loadState === "loaded" && !force) return;
  loadState = "loading";
  try {
    const res = await api.get(LIST_ENDPOINT, { params: { limit: 1000 } });
    const body = res.data ?? {};
    const list = Array.isArray(body.data) ? body.data : [];
    // Merge backend rows ahead of the seed (seed disappears once real data exists).
    bookings = list.length ? list.map(fromApi) : bookings;
    loadState = "loaded";
    emit();
  } catch {
    // Offline / backend not deployed yet → keep the in-session seed.
    loadState = "idle";
  }
}

/** React hook: live list of bookings (hydrates on mount). */
export function useBookings(): CargoBooking[] {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => {
    void loadBookings();
  }, []);
  return snap;
}

function upsertLocal(booking: CargoBooking) {
  const idx = bookings.findIndex((b) => b.id === booking.id);
  bookings = idx >= 0
    ? bookings.map((b) => (b.id === booking.id ? booking : b))
    : [booking, ...bookings];
  emit();
}

let tmpSeq = 0;
function tempId(): string {
  tmpSeq += 1;
  return `local-${Date.now().toString(36)}-${tmpSeq}`;
}

/** Map an engine outcome + quote into a persisted booking record. */
function buildRecord(
  id: string,
  folio: string,
  quote: BookingQuote,
  outcome: Awaited<ReturnType<typeof runBooking>>,
): CargoBooking {
  return {
    id,
    folio,
    route: `${quote.origin || "—"} → ${quote.destination || "—"}`,
    origin: quote.origin,
    destination: quote.destination,
    mode: quote.mode,
    incoterm: quote.incoterm,
    provider: quote.provider || "",
    amount: quote.amount,
    currency: quote.currency,
    status: outcome.status,
    paymentStatus: outcome.paymentStatus,
    bookingRef: outcome.bookingRef,
    awb: outcome.awb,
    bl: outcome.bl,
    documents: outcome.documents,
    events: outcome.events,
    quoteBasis: quote.basis,
    transitDays: quote.transitDays,
    createdAt: new Date().toISOString().slice(0, 10),
  };
}

/**
 * Run the full transaction (provider booking → pago → confirmación) and persist
 * the result. Optimistically inserts the record locally, then tries to save it
 * to the backend (reconciling the id on success). Returns the final booking so
 * the checkout can render the confirmation / failure.
 */
export async function submitBooking(
  quote: BookingQuote,
  payment: PaymentDetails,
  onProgress?: Parameters<typeof runBooking>[2],
): Promise<CargoBooking> {
  const outcome = await runBooking(quote, payment, onProgress);

  const localId = tempId();
  const folio = makeFolio();
  let record = buildRecord(localId, folio, quote, outcome);
  upsertLocal(record);

  // Persist (best-effort). The `events` log goes through verbatim as the audit
  // trail; on success we swap the temp id for the backend-generated one.
  try {
    const res = await api.post(ENDPOINT, {
      folio,
      route: record.route,
      origin: record.origin,
      destination: record.destination,
      mode: record.mode,
      incoterm: record.incoterm,
      provider: record.provider,
      amount: record.amount,
      currency: record.currency,
      status: record.status,
      paymentStatus: record.paymentStatus,
      bookingRef: record.bookingRef,
      awb: record.awb,
      bl: record.bl,
      documents: record.documents,
      events: record.events,
      quoteBasis: record.quoteBasis,
      transitDays: record.transitDays,
    });
    const saved = res.data?.data;
    if (saved?.id && saved.id !== localId) {
      bookings = bookings.filter((b) => b.id !== localId);
      record = fromApi({ ...saved, ...record, id: saved.id });
      upsertLocal(record);
    }
  } catch {
    // Backend unavailable — the booking stays in the in-session store.
  }

  return record;
}
